import { useCallback, useState } from 'react'
import {
  createStyles,
  Header as MantineHeader,
  Group,
  Burger,
  Paper,
  Transition,
  Text,
  Box,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { ThemeToggle } from './ThemeToggle'
import ReactDOM from 'react-dom'
import { DomUtils } from 'src/utils'
import { MenuItem, MenuItems } from 'src/content/links'
import ReactGA from 'react-ga4'

const HEADER_HEIGHT = 80

const useStyles = createStyles((theme, { isMobile }: { isMobile: boolean }) => ({
  root: {
    position: 'fixed',
    top: 0,
    backdropFilter: 'saturate(180%) blur(30px)',
    backgroundColor: theme.colorScheme === 'dark' ? 'rgba(255, 255, 255, 0.07)' : 'hsla(0,0%,100%,.5)',
    borderBottom: 'none',
    boxShadow: theme.shadows.xs,
    paddingInline: isMobile ? 0 : 120,
  },

  dropdown: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    right: 0,
    zIndex: 0,
    borderTopRightRadius: 0,
    borderTopLeftRadius: 0,
    borderTopWidth: 0,
    overflow: 'hidden',

    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    height: '100%',
    paddingInline: theme.spacing.lg,
    paddingBlock: theme.spacing.sm,
  },

  links: {
    [theme.fn.smallerThan('sm')]: {
      display: 'none',
    },
  },

  burger: {
    [theme.fn.largerThan('sm')]: {
      display: 'none',
    },
  },

  link: {
    display: 'block',
    lineHeight: 1,
    padding: 5,
    paddingBlock: 10,
    borderRadius: theme.radius.sm,
    textDecoration: 'none',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.gray[7],
    fontSize: 18,
    fontWeight: 550,

    '&:hover': {
      color: theme.colorScheme === 'dark' ? theme.colors.gray[1] : theme.colors.indigo[7],
    },

    [theme.fn.smallerThan('sm')]: {
      borderRadius: 0,
      padding: theme.spacing.md,
    },
  },

  linkActive: {
    '&, &:hover': {
      // backgroundColor: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).background,
      // color: theme.fn.variant({ variant: 'light', color: theme.primaryColor }).color,
    },
  },
}))

interface HeaderProps {}

export const Header: React.FC<HeaderProps> = props => {
  const [opened, { toggle, close }] = useDisclosure(false)
  const [active, setActive] = useState(MenuItems[0].link)
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)
  const { classes, cx } = useStyles({ isMobile })

  const getElementByClassName = useCallback((className: string) => {
    const element = ReactDOM.findDOMNode(document.getElementsByClassName(className)[0])
    return element
  }, [])

  const scrollToSection = useCallback(
    (className: string, samePage?: boolean) => {
      const element = getElementByClassName(className) as HTMLElement
      if (element && samePage) {
        DomUtils.scrollTo(element, 'bottom', { smooth: true, defer: true })
      }
    },
    [getElementByClassName],
  )

  const navigateToSection = useCallback((href: string) => {
    window.open(href, '_blank')?.focus()
  }, [])

  const handleNavigation = useCallback(
    (menu: MenuItem) => {
      ReactGA.event({
        category: 'Navigation',
        action: `Clicked on ${menu.name}`,
      })
      if (menu.samePage) {
        menu.sectionClassName && scrollToSection(menu.sectionClassName, menu.samePage)
      } else {
        menu.link && navigateToSection(menu.link)
      }
    },
    [scrollToSection, navigateToSection],
  )

  const scrollTop = useCallback(() => {
    DomUtils.scrollToTop({ smooth: true, defer: true })
  }, [])

  const items = MenuItems.map(menu => (
    <a
      key={menu.name}
      href={menu.link}
      className={cx(classes.link, { [classes.linkActive]: active === menu.link })}
      onClick={event => {
        event.preventDefault()
        setActive(menu.link)
        handleNavigation(menu)
        close()
      }}
    >
      {menu.name}
    </a>
  ))

  return (
    <MantineHeader height={HEADER_HEIGHT} className={classes.root}>
      <Box className={classes.header}>
        <UnstyledButton component="a" onClick={scrollTop}>
          <Text fw={'bold'} size={24} color={theme.colorScheme === 'dark' ? theme.colors.gray[2] : theme.black}>
            Tirth Patel
          </Text>
        </UnstyledButton>
        <Group>
          <Group spacing={'lg'} className={classes.links}>
            {items}
          </Group>

          <Group>
            <ThemeToggle />
            <Burger opened={opened} onClick={toggle} className={classes.burger} size="sm" />
          </Group>
        </Group>

        <Transition transition="pop-top-right" duration={200} mounted={opened}>
          {styles => (
            <Paper className={classes.dropdown} withBorder style={styles}>
              {items}
            </Paper>
          )}
        </Transition>
      </Box>
    </MantineHeader>
  )
}
